/**
 * Tiny formatting helpers reused across the UI. Pure functions so they
 * can be exercised by Vitest without rendering a single React tree.
 */

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Coarse "X minutes ago" / "in X hours" formatter. Unit thresholds are
 * picked so every visible row in the notification panel collapses to a
 * stable string at a glance instead of a precise wall-clock time.
 *
 * Purposely English-only — the bell drops back to {@code locale-aware}
 * `Date.toLocaleString` for older items, so this helper only has to
 * cover the last 24 h of activity.
 */
export function formatRelative(input: Date | string | number, now: Date = new Date()): string {
  const t = input instanceof Date ? input.getTime() : new Date(input).getTime();
  if (Number.isNaN(t)) return "";
  const delta = now.getTime() - t;
  const abs = Math.abs(delta);
  const past = delta >= 0;

  const fmt = (n: number, unit: string) => {
    const plural = n === 1 ? unit : unit + "s";
    return past ? `${n} ${plural} ago` : `in ${n} ${plural}`;
  };

  if (abs < 45 * SECOND) return past ? "just now" : "in a moment";
  if (abs < HOUR) return fmt(Math.round(abs / MINUTE), "minute");
  if (abs < DAY) return fmt(Math.round(abs / HOUR), "hour");
  return fmt(Math.round(abs / DAY), "day");
}

/**
 * Human-readable file size in IEC binary units. Used by the materials
 * list and the audit-log download size column.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KiB", "MiB", "GiB", "TiB"];
  let size = bytes / 1024;
  let unitIdx = 0;
  while (size >= 1024 && unitIdx < units.length - 1) {
    size /= 1024;
    unitIdx++;
  }
  return `${size.toFixed(size < 10 ? 1 : 0)} ${units[unitIdx]}`;
}

/**
 * Truncates a string to {@code max} characters and appends an ellipsis
 * when it had to cut. Preserves the original when it already fits so
 * callers can compare lengths to detect truncation.
 */
export function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, Math.max(0, max - 1)) + "…";
}
